import React, { useState } from "react";
import { useEffect as useEffectOriginal } from "react";
import "./App.scss";
import { detectTV, getTVInfo } from "./utils/tvDetector";
import { useUserPreferences } from "./hooks/useUserPreferences";
import { useTranslation } from "react-i18next";
import ExamenSimple from "./ExamenSimple";
import sinsdb from "./data/sinsdb";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Link } from "react-router-dom";
import About from "./About";
import Help from "./Help";
import Prayers from "./Prayers";
import Rosario from "./Rosario";
import Coronilla from "./Coronilla";
import Santos from "./Santos";
import Letanias from "./Letanias";
import LoadingComponent from "./LoadingComponent";
import WelcomeModal from "./WelcomeModal";
import BookmarksPanel from "./BookmarksPanel";
import RosaryIcon from "./RosaryIcon";
import Container from "react-bootstrap/Container";
import Navbar from "react-bootstrap/Navbar";
import Nav from "react-bootstrap/Nav";
import Dropdown from "react-bootstrap/Dropdown";
import "font-awesome/css/font-awesome.min.css";

const App = () => {
  const [selectedSinIds, setSelectedSinIds] = useState([]);
  const [customSins, setCustomSins] = useState([]);
  const [isTV, setIsTV] = useState(false);
  const [showBookmarks, setShowBookmarks] = useState(false);

  // Hook de preferencias del usuario
  const {
    theme,
    fontSize,
    bookmarks,
    changeTheme,
    changeFontSize,
    removeBookmark,
    toggleBookmark,
    isDark
  } = useUserPreferences();

  // Detectar si estamos en una Smart TV
  useEffectOriginal(() => {
    const tvDetected = detectTV();
    setIsTV(tvDetected);

    if (tvDetected) {
      console.log('Modo TV detectado:', getTVInfo());
      document.body.classList.add('tv-mode');
    }

    return () => {
      document.body.classList.remove('tv-mode');
    };
  }, []);

  // Manejo de estado en localStorage
  useEffectOriginal(() => {
    try {
      const storedState = localStorage.getItem("state");
      if (storedState) {
        const parsedState = JSON.parse(storedState);
        setSelectedSinIds(parsedState.selectedSinIds || []);
        setCustomSins(parsedState.customSins || []);
      }
    } catch (error) {
      console.error("Error loading state from localStorage:", error);
    }
  }, []);

  useEffectOriginal(() => {
    const state = { selectedSinIds, customSins };
    localStorage.setItem("state", JSON.stringify(state));
  }, [selectedSinIds, customSins]);

  // Funciones para manejar los pecados
  const addSinId = (id) => setSelectedSinIds((prev) => [...prev, id]);
  const removeSinItem = (sinItem) => {
    if (sinItem.id !== undefined) {
      setSelectedSinIds((prev) => prev.filter((s) => s !== sinItem.id));
    } else {
      setCustomSins((prev) => prev.filter((s) => s !== sinItem.text));
    }
  };
  const addCustomSin = (text) => setCustomSins((prev) => [...prev, text]);
  const removeCustomSin = (text) => setCustomSins((prev) => prev.filter((s) => s !== text));
  const clearAll = () => {
    setSelectedSinIds([]);
    setCustomSins([]);
  };
  const handleThemeToggle = () => changeTheme(isDark ? 'light' : 'dark');

  // Navbar Componente
  const AppNavbar = () => {
    const { t, i18n } = useTranslation();
    const location = useLocation();
    const [showMobileMenu, setShowMobileMenu] = useState(false);

    const languages = [
      { code: "en", flag: "🇺🇸", name: "English" },
      { code: "es", flag: "🇪🇸", name: "Español" },
      { code: "de", flag: "🇩🇪", name: "Deutsch" },
      { code: "it", flag: "🇮🇹", name: "Italiano" },
      { code: "pt-BR", flag: "🇧🇷", name: "Português" }
    ];
    const currentLang = languages.find(l => l.code === i18n.resolvedLanguage) || languages[0];

    const isDarkMode = isDark;
    const navigate = useNavigate();

    // Enlaces principales para móvil (todos visibles, sin dropdowns)
    const mobileMainLinks = [
      { path: "/", icon: "📋", label: "navbar.examen" },
      { path: "/prayers", icon: "🙏", label: "navbar.prayers" },
      { path: "/rosario", icon: <RosaryIcon width={20} height={30} />, label: "navbar.rosary" },
      { path: "/coronilla", icon: "🕊️", label: "navbar.coronilla" },
      { path: "/santos", icon: "👼", label: "navbar.saints" },
      { path: "/litany", icon: "📖", label: "navbar.litany" },
    ];

    // Enlaces de info para móvil
    const mobileInfoLinks = [
      { path: "/help", icon: "❓", label: "navbar.help" },
      { path: "/about", icon: "💡", label: "navbar.about" },
    ];

    const handleNavClick = (path) => {
      navigate(path);
      setShowMobileMenu(false);
    };



    return (
      <Navbar
        sticky="top"
        expand="lg"
        data-theme={isDarkMode ? "dark" : "light"}
        className={`app-navbar ${isDarkMode ? 'navbar-dark-mode' : 'navbar-light-mode'}`}
        style={{
          height: "3.5rem",
          boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
          padding: "0 1rem"
        }}
      >
        <Container fluid>
          <Navbar.Brand
            as={Link}
            to="/"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              color: "white !important",
              textDecoration: "none"
            }}
          >
            <span style={{ fontSize: "1.6rem" }}>⛪</span>
            <span style={{
              fontFamily: '"EB Garamond", serif',
              fontSize: "1.4rem",
              fontWeight: 600,
              color: "white"
            }}>Confiésalo</span>
          </Navbar.Brand>

          <Navbar.Toggle
            aria-controls="navbar-content"
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            style={{
              borderColor: "rgba(255, 255, 255, 0.3)",
              color: "white"
            }}
          />

          <Navbar.Collapse in={showMobileMenu} id="navbar-content">
            <Nav className="d-none d-lg-flex me-auto" style={{ display: "flex", gap: "0.25rem", alignItems: "center" }}>
              <Nav.Link
                as={Link}
                to="/"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.4rem",
                  padding: "0.5rem 0.85rem",
                  borderRadius: "0.5rem",
                  color: location.pathname === "/" ? "white" : "rgba(255, 255, 255, 0.9)",
                  fontSize: "0.95rem",
                  fontWeight: location.pathname === "/" ? "600" : "500",
                  textDecoration: "none",
                  background: location.pathname === "/" ? "rgba(255, 255, 255, 0.25)" : "transparent"
                }}
              >
                <span style={{ fontSize: "1rem" }}>📋</span>
                {t("navbar.examen")}
              </Nav.Link>

              <Nav.Link
                as={Link}
                to="/prayers"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.4rem",
                  padding: "0.5rem 0.85rem",
                  borderRadius: "0.5rem",
                  color: location.pathname === "/prayers" ? "white" : "rgba(255, 255, 255, 0.9)",
                  fontSize: "0.95rem",
                  fontWeight: location.pathname === "/prayers" ? "600" : "500",
                  textDecoration: "none",
                  background: location.pathname === "/prayers" ? "rgba(255, 255, 255, 0.25)" : "transparent"
                }}
              >
                <span style={{ fontSize: "1rem" }}>🙏</span>
                {t("navbar.prayers")}
              </Nav.Link>

              <Dropdown>
                <Dropdown.Toggle
                  variant="link"
                  id="resources-dropdown"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.4rem",
                    padding: "0.5rem 0.85rem",
                    borderRadius: "0.5rem",
                    color: "rgba(255, 255, 255, 0.9)",
                    fontSize: "0.95rem",
                    fontWeight: "500",
                    textDecoration: "none",
                    background: "transparent",
                    border: "none"
                  }}
                >
                  <span style={{ fontSize: "1rem" }}>✝️</span>
                  {t("navbar.resources")}
                </Dropdown.Toggle>
                <Dropdown.Menu
                  align="end"
                  className="navbar-dropdown-menu"
                  style={{
                    border: "none",
                    borderRadius: "0.75rem",
                    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
                    padding: "0.5rem",
                    minWidth: "180px"
                  }}
                >
                  <Dropdown.Item
                    as={Link}
                    to="/rosario"
                    className="navbar-dropdown-item"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      padding: "0.55rem 0.75rem",
                      borderRadius: "0.4rem",
                      textDecoration: "none"
                    }}
                  >
                    <RosaryIcon width={18} height={27} />
                    {t("navbar.rosary")}
                  </Dropdown.Item>
                  <Dropdown.Item
                    as={Link}
                    to="/coronilla"
                    className="navbar-dropdown-item"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      padding: "0.55rem 0.75rem",
                      borderRadius: "0.4rem",
                      textDecoration: "none"
                    }}
                  >
                    <span style={{ fontSize: "0.95rem" }}>🕊️</span>
                    {t("navbar.coronilla")}
                  </Dropdown.Item>
                  <Dropdown.Item
                    as={Link}
                    to="/santos"
                    className="navbar-dropdown-item"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      padding: "0.55rem 0.75rem",
                      borderRadius: "0.4rem",
                      textDecoration: "none"
                    }}
                  >
                    <span style={{ fontSize: "0.95rem" }}>👼</span>
                    {t("navbar.saints")}
                  </Dropdown.Item>
                  <Dropdown.Item
                    as={Link}
                    to="/litany"
                    className="navbar-dropdown-item"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      padding: "0.55rem 0.75rem",
                      borderRadius: "0.4rem",
                      textDecoration: "none"
                    }}
                  >
                    <span style={{ fontSize: "0.95rem" }}>📖</span>
                    {t("navbar.litany")}
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>

              <Dropdown>
                <Dropdown.Toggle
                  variant="link"
                  id="info-dropdown"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.4rem",
                    padding: "0.5rem 0.85rem",
                    borderRadius: "0.5rem",
                    color: "rgba(255, 255, 255, 0.9)",
                    fontSize: "0.95rem",
                    fontWeight: "500",
                    textDecoration: "none",
                    background: "transparent",
                    border: "none"
                  }}
                >
                  <span style={{ fontSize: "1rem" }}>ℹ️</span>
                  {t("navbar.info")}
                </Dropdown.Toggle>
                <Dropdown.Menu
                  align="end"
                  className="navbar-dropdown-menu"
                  style={{
                    border: "none",
                    borderRadius: "0.75rem",
                    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
                    padding: "0.5rem",
                    minWidth: "160px"
                  }}
                >
                  <Dropdown.Item
                    as={Link}
                    to="/help"
                    className="navbar-dropdown-item"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      padding: "0.55rem 0.75rem",
                      borderRadius: "0.4rem",
                      textDecoration: "none"
                    }}
                  >
                    <span style={{ fontSize: "0.95rem" }}>❓</span>
                    {t("navbar.help")}
                  </Dropdown.Item>
                  <Dropdown.Item
                    as={Link}
                    to="/about"
                    className="navbar-dropdown-item"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      padding: "0.55rem 0.75rem",
                      borderRadius: "0.4rem",
                      textDecoration: "none"
                    }}
                  >
                    <span style={{ fontSize: "0.95rem" }}>💡</span>
                    {t("navbar.about")}
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </Nav>

            {/* MÓVIL: Navegación simplificada - todos los enlaces visibles */}
            <div className="d-lg-none w-100">
              {/* Enlaces principales */}
              <div className="mobile-nav-section">
                {mobileMainLinks.map((link) => (
                  <div
                    key={link.path}
                    className={`mobile-nav-item ${location.pathname === link.path ? 'active' : ''}`}
                    onClick={() => handleNavClick(link.path)}
                  >
                    <span className="mobile-nav-icon">{link.icon}</span>
                    <span className="mobile-nav-label">{t(link.label)}</span>
                  </div>
                ))}
              </div>

              {/* Enlaces de info */}
              <div className="mobile-nav-section mobile-nav-info">
                {mobileInfoLinks.map((link) => (
                  <div
                    key={link.path}
                    className={`mobile-nav-item ${location.pathname === link.path ? 'active' : ''}`}
                    onClick={() => handleNavClick(link.path)}
                  >
                    <span className="mobile-nav-icon">{link.icon}</span>
                    <span className="mobile-nav-label">{t(link.label)}</span>
                  </div>
                ))}
              </div>
            </div>
                        <div className="navbar-right-panel" style={{ display: "flex", alignItems: "center", gap: "0.4rem", marginLeft: "auto" }}>
              <Dropdown>
                <Dropdown.Toggle
                  variant="link"
                  id="lang-dropdown"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "2.3rem",
                    height: "2.3rem",
                    padding: 0,
                    color: "white",
                    background: "rgba(255, 255, 255, 0.08)",
                    border: "none",
                    borderRadius: "0.5rem"
                  }}
                >
                  <span style={{ fontSize: "1.2rem" }}>{currentLang.flag}</span>
                </Dropdown.Toggle>
                <Dropdown.Menu
                  align="center"
                  className="navbar-dropdown-menu"
                  style={{
                    border: "none",
                    borderRadius: "0.75rem",
                    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
                    padding: "0.5rem",
                    minWidth: "60px"
                  }}
                >
                  {languages.map((lang) => (
                    <Dropdown.Item
                      key={lang.code}
                      onClick={() => i18n.changeLanguage(lang.code)}
                      className={`navbar-dropdown-item ${i18n.resolvedLanguage === lang.code ? 'active' : ''}`}
                      style={{
                        display: "inline-flex",
                        justifyContent: "center",
                        alignItems: "center",
                        padding: "0.5rem",
                        borderRadius: "0.4rem"
                      }}
                    >
                      <span style={{ fontSize: "1.3rem" }}>{lang.flag}</span>
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>

              <button
                onClick={() => setShowBookmarks(true)}
                style={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "2.3rem",
                  height: "2.3rem",
                  padding: 0,
                  border: "none",
                  borderRadius: "0.5rem",
                  background: "rgba(255, 255, 255, 0.08)",
                  color: "white",
                  cursor: "pointer"
                }}
              >
                <span style={{ fontSize: "1.1rem" }}>🔖</span>
                {bookmarks.length > 0 && (
                  <span style={{
                    position: "absolute",
                    top: "-3px",
                    right: "-3px",
                    minWidth: "1.1rem",
                    height: "1.1rem",
                    padding: "0 0.25rem",
                    background: "#ffc107",
                    color: "#3c005a",
                    fontSize: "0.65rem",
                    fontWeight: "700",
                    borderRadius: "1rem"
                  }}>{bookmarks.length}</span>
                )}
              </button>

              <button
                onClick={handleThemeToggle}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "2.3rem",
                  height: "2.3rem",
                  padding: 0,
                  border: "none",
                  borderRadius: "0.5rem",
                  background: "rgba(255, 255, 255, 0.08)",
                  color: "white",
                  cursor: "pointer"
                }}
              >
                <span style={{ fontSize: "1.1rem" }}>{isDarkMode ? "☀️" : "🌙"}</span>
              </button>

              <Dropdown>
                <Dropdown.Toggle
                  variant="link"
                  id="settings-dropdown"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "2.3rem",
                    height: "2.3rem",
                    padding: 0,
                    color: "white",
                    background: "rgba(255, 255, 255, 0.08)",
                    border: "none",
                    borderRadius: "0.5rem"
                  }}
                >
                  <span style={{ fontSize: "1.1rem" }}>⚙️</span>
                </Dropdown.Toggle>
                <Dropdown.Menu
                  align="end"
                  className="navbar-dropdown-menu"
                  style={{
                    border: "none",
                    borderRadius: "0.75rem",
                    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
                    padding: "0.5rem",
                    minWidth: "160px"
                  }}
                >
                  <Dropdown.Header className="navbar-dropdown-header">{t("navbar.settings")}</Dropdown.Header>
                  <Dropdown.Divider className="navbar-dropdown-divider" />
                  <Dropdown.Item
                    active={fontSize === "small"}
                    onClick={() => changeFontSize("small")}
                    className="navbar-dropdown-item"
                    style={{
                      padding: "0.55rem 0.75rem",
                      borderRadius: "0.4rem"
                    }}
                  >
                    A- {t("preferences.font_small")}
                  </Dropdown.Item>
                  <Dropdown.Item
                    active={fontSize === "normal"}
                    onClick={() => changeFontSize("normal")}
                    className="navbar-dropdown-item"
                    style={{
                      padding: "0.55rem 0.75rem",
                      borderRadius: "0.4rem"
                    }}
                  >
                    A {t("preferences.font_normal")}
                  </Dropdown.Item>
                  <Dropdown.Item
                    active={fontSize === "large"}
                    onClick={() => changeFontSize("large")}
                    className="navbar-dropdown-item"
                    style={{
                      padding: "0.55rem 0.75rem",
                      borderRadius: "0.4rem"
                    }}
                  >
                    A+ {t("preferences.font_large")}
                  </Dropdown.Item>
                  <Dropdown.Divider className="navbar-dropdown-divider" />
                  <Dropdown.Item
                    onClick={clearAll}
                    className="navbar-dropdown-item navbar-dropdown-danger"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      padding: "0.55rem 0.75rem",
                      borderRadius: "0.4rem"
                    }}
                  >
                    <span style={{ fontSize: "0.95rem" }}>🗑️</span>
                    {t("navbar.clear")}
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </Navbar.Collapse>
        </Container>
      </Navbar>
    );
  };

  // Layout con Navbar y contenido
  const AppLayout = ({ children }) => {
    const navigate = useNavigate();

    return (
      <div className={`App${isTV ? ' tv-mode' : ''} theme-${theme}`}>
        {showBookmarks && (
          <BookmarksPanel
            bookmarks={bookmarks}
            onRemove={removeBookmark}
            onClose={() => setShowBookmarks(false)}
            onNavigate={(bookmark) => {
              setShowBookmarks(false);
              // Navegación según tipo de marcador
              if (bookmark.type === 'prayer') navigate('/prayers');
            }}
          />
        )}
        <AppNavbar />
        <main style={{ minHeight: `calc(100vh - 3.5rem)` }}>
          {children}
        </main>
        <WelcomeModal />
      </div>
    );
  };

  return (
    <Routes>
      <Route path="/" element={
        <AppLayout>
          <ExamenSimple
            sinsdb={sinsdb}
            selectedSinIds={selectedSinIds}
            onAddSinId={addSinId}
            onRemoveSinItem={removeSinItem}
            customSins={customSins}
            onRemoveCustomSin={removeCustomSin}
            onAddCustomSin={addCustomSin}
            isTV={isTV}
          />
        </AppLayout>
      } />
      <Route path="/prayers" element={
        <AppLayout>
          <Prayers isTV={isTV} bookmarks={bookmarks} toggleBookmark={toggleBookmark} />
        </AppLayout>
      } />
      <Route path="/rosario" element={
        <AppLayout>
          <Rosario isTV={isTV} />
        </AppLayout>
      } />
      <Route path="/coronilla" element={
        <AppLayout>
          <Coronilla isTV={isTV} />
        </AppLayout>
      } />
      <Route path="/santos" element={
        <AppLayout>
          <Santos isTV={isTV} />
        </AppLayout>
      } />
      <Route path="/litany" element={
        <AppLayout>
          <Letanias isTV={isTV} />
        </AppLayout>
      } />
      <Route path="/help" element={
        <AppLayout>
          <Help isTV={isTV} />
        </AppLayout>
      } />
      <Route path="/about" element={
        <AppLayout>
          <About isTV={isTV} />
        </AppLayout>
      } />
    </Routes>
  );
};

export default function WrappedApp() {
  return (
    <BrowserRouter>
      <React.Suspense fallback={<LoadingComponent />}>
        <App />
      </React.Suspense>
    </BrowserRouter>
  );
}
